/* eslint-disable max-len */
/**
 * Dependencies:
 * - Custom select component
 * - Fetch cache (theme.fetchCache)
 * - Quickbuy content transformer (theme.quickBuy.convertToQuickBuyContent)
 *
 * Required translation strings:
 * - noStock
 * - noVariant
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.quickBuyContainer = this.closest('.js-quickbuy');
      this.featuredProductContainer = this.closest('.cc-featured-product');
      this.section = this.quickBuyContainer
                     || this.featuredProductContainer
                     || this.closest('.shopify-section');
      this.optionSelectors = this.querySelectorAll('.option-selector:not(.option-selector--custom)');
      this.productAvailable = this.classList.contains('variant-picker--product-available');
      this.variant = this.getVariantData();
      this.variantRequestId = 0;

      this.restoreIntegratedHyper2Selection();
      this.addListeners();
      this.updateVariantContent();
      if (!this.quickBuyContainer && !this.featuredProductContainer) {
        setTimeout(this.applySearchParams.bind(this), 0);
      }
      this.setAttribute('loaded', '');
    }

    addListeners() {
      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      this.addEventListener('change', this.boundHandleVariantChange);

      this.boundHandleLabelMouseEnter = this.handleLabelMouseEnter.bind(this);
      this.querySelectorAll('.opt-label, .custom-select__option').forEach((el) => {
        el.addEventListener('mouseenter', this.boundHandleLabelMouseEnter);
        el.addEventListener('touchstart', this.boundHandleLabelMouseEnter, { passive: true, once: true });
        el.addEventListener('mouseleave', VariantPicker.handleLabelMouseLeave);
      });
    }

    removeListeners() {
      this.removeEventListener('change', this.boundHandleVariantChange);
      this.querySelectorAll('.opt-label, .custom-select__option').forEach((el) => {
        el.removeEventListener('mouseenter', this.boundHandleLabelMouseEnter);
        el.removeEventListener('touchstart', this.boundHandleLabelMouseEnter);
        el.removeEventListener('mouseleave', VariantPicker.handleLabelMouseLeave);
      });
    }

    /**
     * Update parts of this component that require JS to display correctly.
     */
    updateVariantContent() {
      this.updateStatusClasses();
      this.updateAddToCartButton();
      this.updateSwatchLabel();
      this.updateAvailability();
      this.updateWinspaceHandlebarProperties();
      this.initWinspaceWheelsetProperties();
    }

    getProductForm() {
      return this.section.querySelector('.js-product-form');
    }

    /**
     * Handles 'mouseenter' events on option labels. Preloads links.
     * @param {object} evt - Event object.
     */
    handleLabelMouseEnter(evt) {
      const label = evt.currentTarget;
      if (label.htmlFor && document.getElementById(label.htmlFor)?.matches('[data-avenger-required]')) return;
      if (label.dataset.preloaded) return; // Preload completed
      if (label.dataset.preloadTimeout) return; // Preload waiting
      if (label.matches('.opt-btn:checked + .opt-label, [aria-selected="true"]')) return; // Already selected

      // Build option array if clicked
      const input = label.htmlFor ? document.getElementById(label.htmlFor) : label;
      if (input.matches?.('[data-hyper2-integrated-option], [data-hyper2-base-choice]')) return;
      const optionIds = this.getSelectedOptionIds();
      const inputOptionIndex = Array.from(this.optionSelectors).indexOf(input.closest('.option-selector'));
      optionIds[inputOptionIndex] = input.dataset.valueId;

      // If all options are selected
      if (optionIds.indexOf(null) === -1) {
        // Wait to predict click-intent and preload link
        label.dataset.preloadTimeout = setTimeout(() => {
          label.dataset.preloaded = true;
          label.removeAttribute('data-preload-timeout');
          const combinedProductUrl = input.dataset.productUrl;
          const url = this.constructVariantUrl(
            combinedProductUrl,
            optionIds,
            input.dataset.variantId
          );
          theme.fetchCache.preload(url.toString());
        }, 350);
      }
    }

    /**
     * Handles 'mouseleave' events on option labels. Cancels link preload if in/out fast.
     * @param {object} evt - Event object.
     */
    static handleLabelMouseLeave(evt) {
      const label = evt.currentTarget;
      clearTimeout(label.dataset.preloadTimeout);
      label.removeAttribute('data-preload-timeout');
    }

    /**
     * Create a URL for fetching a product or variant.
     * @param {string} combinedProductUrl - URL for a combined product. Optional.
     * @param {Array} optionIds - Selected option IDs to include in the URL. Optional.
     * @param {string} variantId - Variant ID to use if not a combined product. Optional.
     * @returns {URL} Variant URL
     */
    constructVariantUrl(combinedProductUrl, optionIds, variantId) {
      let url = null;
      if (combinedProductUrl) {
        url = new URL(combinedProductUrl, window.location.origin);
        url.searchParams.set('option_values', optionIds.join(','));
      } else {
        url = new URL(this.dataset.url, window.location.origin);
      }
      const sectionIds = [this.dataset.sectionId];
      if (!this.quickBuyContainer && !this.featuredProductContainer) {
        document.querySelectorAll('.cc-variant-dependent-section [data-section-id]').forEach((e) => {
          if (e.closest('.rte')) return;
          sectionIds.push(e.dataset.sectionId);
        });
      }
      url.searchParams.set('sections', sectionIds.join(','));
      if (combinedProductUrl || !variantId || optionIds.includes(null)) {
        url.searchParams.set('option_values', optionIds.join(','));
      } else {
        url.searchParams.set('variant', variantId);
      }
      return url;
    }

    getIntegratedHyper2StateKey() {
      const context = this.quickBuyContainer ? 'quick-buy' : 'product-page';
      return `${context}|${this.dataset.sectionId || ''}|${this.dataset.url || ''}`;
    }

    setIntegratedHyper2State(selected) {
      VariantPicker.integratedHyper2Selections = VariantPicker.integratedHyper2Selections || new Set();
      const key = this.getIntegratedHyper2StateKey();
      if (selected) {
        VariantPicker.integratedHyper2Selections.add(key);
      } else {
        VariantPicker.integratedHyper2Selections.delete(key);
      }
    }

    restoreIntegratedHyper2Selection() {
      const selections = VariantPicker.integratedHyper2Selections;
      if (!selections?.has(this.getIntegratedHyper2StateKey())) return;

      const option = this.querySelector('[data-hyper2-integrated-option]');
      if (option) option.checked = true;
      this.syncIntegratedHyper2Classes();
    }

    syncIntegratedHyper2Classes() {
      this.querySelectorAll('[data-winspace-handlebar-selector]').forEach((selector) => {
        selector.classList.toggle(
          'is-hyper2-selected',
          Boolean(selector.querySelector('[data-hyper2-integrated-option]:checked'))
        );
      });
    }

    getIntegratedHyper2BaseOption(option) {
      const selector = option.closest('[data-winspace-handlebar-selector]');
      if (!selector || selector.classList.contains('option-selector--custom')) return null;

      const nativeOptions = Array.from(selector.querySelectorAll('.js-option'));
      const priorities = [
        (value) => value.includes('sin manillar'),
        (value) => value.includes('zero'),
        (value) => value.includes('adventure'),
        (value) => !value.includes('hyper')
      ];

      for (const matches of priorities) {
        const match = nativeOptions.find((input) => matches(input.value.toLowerCase().trim()));
        if (match) return match;
      }

      return nativeOptions[0] || null;
    }

    /**
     * Handles 'change' events on the variant picker element.
    * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      if (evt.target.matches?.('[data-avenger-required]')) return;
      let variantTarget = evt.target;
      // Quick Pro configurator controls carry line-item properties, not variant
      // options. Without this the picker would refetch the variant URL and
      // re-render, throwing the selection back to its default.
      if (evt.target.closest?.('[data-quickpro-setup]')) return;
      if (evt.target.closest?.('[data-winspace-property-width], [data-winspace-property-stem]')) {
        this.dispatchEvent(new CustomEvent('on:hyper2:change', { bubbles: true }));
        return;
      }
      const integratedHyper2Option = evt.target.matches?.('[data-hyper2-integrated-option]');
      const baseHandlebarChoice = evt.target.matches?.('[data-hyper2-base-choice]');
      const nativeHandlebarChoice = evt.target.matches?.('.js-option')
        && evt.target.closest('[data-winspace-handlebar-selector]');

      if (integratedHyper2Option) {
        this.setIntegratedHyper2State(true);
        const baseOption = this.getIntegratedHyper2BaseOption(evt.target);
        const baseWasSelected = Boolean(baseOption?.checked);
        if (baseOption) {
          baseOption.checked = true;
          variantTarget = baseOption;
        }
        this.syncIntegratedHyper2Classes();
        this.updateWinspaceHandlebarProperties();
        this.dispatchEvent(new CustomEvent('on:hyper2:change', { bubbles: true }));
        if (!baseOption || baseWasSelected) return;
      } else if (baseHandlebarChoice) {
        this.setIntegratedHyper2State(false);
        this.syncIntegratedHyper2Classes();
        this.updateWinspaceHandlebarProperties();
        this.dispatchEvent(new CustomEvent('on:hyper2:change', { bubbles: true }));
        return;
      } else if (nativeHandlebarChoice) {
        this.setIntegratedHyper2State(false);
        const integratedOption = nativeHandlebarChoice
          .closest('[data-winspace-handlebar-selector]')
          .querySelector('[data-hyper2-integrated-option]');
        if (integratedOption) integratedOption.checked = false;
        this.syncIntegratedHyper2Classes();
        this.dispatchEvent(new CustomEvent('on:hyper2:change', { bubbles: true }));
      }

      const selectedOptionIds = this.getSelectedOptionIds();

      this.updateWinspaceHandlebarProperties();
      // Immediately update swatch label
      this.updateSwatchLabel();

      // Construct new variant URL and fetch it, using theme cache
      const combinedProductUrl = variantTarget.dataset.productUrl || evt.detail?.productUrl;
      const variantId = variantTarget.dataset.variantId || evt.detail?.variantId;
      const url = this.constructVariantUrl(combinedProductUrl, selectedOptionIds, variantId);
      this.variantRequestId += 1;
      const currentRequestId = this.variantRequestId;

      theme.fetchCache.fetch(url.toString())
        .then((responseText) => {
          if (currentRequestId !== this.variantRequestId) return;

          const responseData = JSON.parse(responseText);

          // Update content
          Object.entries(responseData).forEach(([section, htmlStr]) => {
            const html = new DOMParser().parseFromString(htmlStr, 'text/html');
            const sectionId = `shopify-section-${section}`;
            let target = this.section;
            let newContent = html.getElementById(sectionId);

            if (this.section.id !== sectionId) {
              if (!this.quickBuyContainer) {
                target = document.getElementById(sectionId);
              } else {
                const quickBuyTemplate = html.getElementById('quick-buy-template')?.content.querySelector('.js-product');
                if (quickBuyTemplate) newContent = quickBuyTemplate;
              }
            }

            this.updateContent(target, newContent, combinedProductUrl);
          });

          // Restore focus
          if (!evt.detail?.noFocus) {
            document.querySelector(`#${evt.target.id}-button, input#${evt.target.id}`)?.focus();
          }

          // Update URL and announce changes
          const newVariantPicker = this.section.querySelector('variant-picker');
          setTimeout(() => newVariantPicker.announceChange(), 10);
        });
    }

    /**
     * Announces the current variant after a variant change.
     */
    announceChange() {
      const selectedOptionIds = this.getSelectedOptionIds();
      if (!selectedOptionIds.includes(null)) {
        this.updateUrl();
      }

      this.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: {
          form: this.getProductForm(),
          variant: this.variant,
          selectedOptions: this.getSelectedOptions()
        }
      }));
    }

    /**
     * Replace content when the variant or product changes.
     * @param {Element} target - Target container for replacing content within.
     * @param {document} newContent - New content containing elements to use.
     * @param {boolean} productChange - Product is changing.
     */
    updateContent(target, newContent, productChange) {
      // If something's not right, do nothing
      if (!target || !newContent) return;

      // Preselection - only update certain option value attributes
      if (this.getSelectedOptions().indexOf(null) !== -1 && !productChange) {
        target.querySelectorAll('.js-option[data-value-id]').forEach((input) => {
          const newInput = newContent.querySelector(`.js-option[data-value-id="${input.dataset.valueId}"]`);
          input.className = newInput.className;
          if (newInput.dataset.variantId) {
            input.dataset.variantId = newInput.dataset.variantId;
          } else {
            delete input.dataset.variantId;
          }
          if (input.classList.contains('custom-select__option')) {
            input.innerHTML = newInput.innerHTML;
          }
        });
        this.updateStatusClasses();
        return;
      }

      let replaceSelector = '[data-dynamic-variant-content]';
      if (productChange) {
        replaceSelector += ', [data-dynamic-product-content]';
      }
      const toReplaceList = target.querySelectorAll(replaceSelector);

      toReplaceList.forEach((toReplace) => {
        const replaceWith = toReplace.dataset.dynamicVariantContent
          ? newContent.querySelector(`[data-dynamic-variant-content="${CSS.escape(toReplace.dataset.dynamicVariantContent)}"]`)
          : newContent.querySelector(`[data-dynamic-product-content="${CSS.escape(toReplace.dataset.dynamicProductContent)}"]`);
        if (!replaceWith) return;

        // Dispatch 'on:variant:before-replace-element' event before replacing element
        document.dispatchEvent(new CustomEvent('on:variant:before-replace-element', {
          bubbles: true,
          detail: {
            toReplace,
            replaceWith
          }
        }));

        // Preserve custom-option selections when native variants replace markup.
        const sourceOptions=toReplace.querySelector('raceit-product-options');
        const targetOptions=replaceWith.querySelector('raceit-product-options');
        if(sourceOptions?.ready && targetOptions)targetOptions.dataset.optionsRestore=JSON.stringify({selections:sourceOptions.selections,visible:[...sourceOptions.querySelectorAll('[data-options-group]')].filter(group=>!group.hidden).map(group=>group.dataset.optionsGroup)});

        // If in quick buy, content needs mutating
        if (this.quickBuyContainer && theme.quickBuy) {
          theme.quickBuy.convertToQuickBuyContent(
            replaceWith,
            this.quickBuyContainer.dataset.productUrl
          );
        }

        // Transfer basic input values
        toReplace.querySelectorAll('input[id]:not([type="hidden"]), select[id]').forEach((sourceInput) => {
          if (sourceInput.matches('.custom-select__native')) {
            const customSelectId = sourceInput.id.replace('-native', '');
            const targetInput = replaceWith.querySelector(`#${CSS.escape(customSelectId)}`);
            if (!targetInput) return;
            const option = targetInput.querySelector(`.js-option[data-value="${CSS.escape(sourceInput.value)}"]`);
            if (!option) return;
            setTimeout(() => {
              targetInput.selectOption(option);
            }, 0);
          } else {
            const targetInput = replaceWith.querySelector(`#${CSS.escape(sourceInput.id)}`);
            if (!targetInput) return;
            if (sourceInput.type === 'radio' || sourceInput.type === 'checkbox') {
              targetInput.toggleAttribute('checked', sourceInput.checked);
            } else {
              targetInput.value = sourceInput.value;
            }
          }
        });

        // Replace content
        const isIdInput = replaceWith.matches('input[name="id"]');
        if (isIdInput) {
          // Update ID input - preserving listeners
          [...replaceWith.attributes].forEach(
            (attr) => toReplace.setAttribute(attr.name, attr.value)
          );
          toReplace.value = replaceWith.value;
          // Defer change event until all forms are accurate
          setTimeout(() => {
            toReplace.dispatchEvent(new Event('change', { bubbles: true }));
          }, 0);
        } else {
          // Remember toReplace becomes detached
          toReplace.replaceWith(replaceWith);
        }

        // Do not mistake input value changes for variant changes - remove listeners
        replaceWith.querySelectorAll('variant-picker').forEach((el) => el.removeListeners());

        // Set values of any unselected <custom-select> elements again
        // A fetched variant may not appear selected
        const selectedOptionIds = this.getSelectedOptionIds();
        selectedOptionIds.forEach((optionId) => {
          const toSelect = replaceWith.querySelector(`.option-selector .custom-select__option[data-value-id="${optionId}"]:not([aria-selected="true"])`);
          if (toSelect) {
            setTimeout(() => {
              toSelect.closest('custom-select').selectOption(toSelect);
            }, 0);
          }
        });

        // Update variant picker after potential changes are applied
        setTimeout(() => {
          replaceWith.querySelectorAll('variant-picker').forEach((el) => {
            el.addListeners();
            el.updateVariantContent();
          });
        }, 10);
      });
    }

    /**
     * Get selected value for an option selector.
     * @param {Element} selector - Option selector element.
     * @returns {?string}
     */
    getSelectedValueForSelector(selector) {
      if (!selector) return null;

      const integratedHyper2 = selector.querySelector('[data-hyper2-integrated-option]:checked');
      if (integratedHyper2) return integratedHyper2.value;

      if (selector.dataset.selectorType === 'dropdown') {
        const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
        return selected && selected.dataset.value !== '' ? selected.dataset.value : null;
      }

      const selected = selector.querySelector('.js-option:checked');
      return selected ? selected.value : null;
    }

    /**
     * Get Winspace handlebar config key from selected value.
     * @param {?string} value - Selected handlebar value.
     * @returns {?string}
     */
    getWinspaceHandlebarConfigKey(value) {
      if (!value) return null;

      const normalizedValue = value.toLowerCase().trim();
      if (!normalizedValue || normalizedValue.includes('sin manillar')) return null;
      // Incolor AEROWAV — check gravel first (more specific match)
      if (normalizedValue.includes('aerowav gravel')) return 'aerowav gravel';
      if (normalizedValue.includes('aerowav')) return 'aerowav';
      // Winspace — check the more specific "hyper2" match before the generic "hyper" one
      if (normalizedValue.includes('adventure')) return 'adventure';
      if (normalizedValue.includes('zero')) return 'zero sl';
      if (normalizedValue.includes('hyper2') || normalizedValue.includes('hyper 2')) return 'hyper2';
      if (normalizedValue.includes('hyper')) return 'hyper';
      return null;
    }

    /** Return the selected value from a shared cockpit property button group. */
    getWinspacePropertyValue(field) {
      if (!field || field.disabled) return '';
      return field.querySelector('[data-raceit-property-option]:checked')?.value || '';
    }

    /** Enable or disable a complete property group and every radio it owns. */
    setWinspacePropertyEnabled(field, enabled) {
      if (!field) return;
      field.disabled = !enabled;
      field.classList.toggle('is-disabled', !enabled);
      field.setAttribute('aria-disabled', enabled ? 'false' : 'true');
      field.querySelectorAll('[data-raceit-property-option]').forEach((input) => {
        input.disabled = !enabled;
        if (!enabled) input.checked = false;
      });
    }

    /**
     * Rebuild a cockpit property as native-style variant buttons. The current
     * selection survives a handlebar-model change when it is still valid.
     * @param {HTMLFieldSetElement} field - Shared property picker fieldset.
     * @param {Array} values - Values to render.
     */
    populateWinspacePropertyField(field, values) {
      if (!field) return;

      const currentValue = this.getWinspacePropertyValue(field);
      const choices = field.querySelector('[data-raceit-property-choices]');
      if (!choices) return;
      choices.textContent = '';

      values.forEach((value, index) => {
        const optionId = `${field.id}-option-${index + 1}`;
        const input = document.createElement('input');
        input.id = optionId;
        input.className = 'opt-btn visually-hidden focus-label';
        input.type = 'radio';
        input.name = field.id;
        input.value = value;
        input.dataset.raceitPropertyOption = '';
        input.setAttribute('aria-describedby', `${field.id}-error`);
        input.disabled = field.disabled;
        input.checked = value === currentValue;

        const label = document.createElement('label');
        label.className = 'opt-label opt-label--btn btn relative text-center';
        label.htmlFor = optionId;
        const text = document.createElement('span');
        text.textContent = String(value).replace(/\s*mm\b/i, ' MM');
        label.appendChild(text);

        choices.appendChild(input);
        choices.appendChild(label);
      });
    }

    /**
     * Create or update a hidden Winspace line item property input in the product form.
     * @param {?HTMLFormElement} productForm - Product form element.
     * @param {string} propertyName - Line item property name.
     * @param {string} value - Line item property value.
     */
    upsertWinspacePropertyInput(productForm, propertyName, value) {
      if (!productForm) return;

      const selector = `input[type="hidden"][data-winspace-property-name="${CSS.escape(propertyName)}"]`;
      let input = productForm.querySelector(selector);

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${propertyName}]`;
        input.dataset.winspacePropertyName = propertyName;
        productForm.appendChild(input);
      }

      input.value = value || '';
    }

    /**
     * Ensure Winspace property values are synced to hidden form inputs.
     * @param {?HTMLFormElement} productForm - Product form element.
     * @param {?HTMLFieldSetElement} widthField - Width button group.
     * @param {?HTMLFieldSetElement} stemField - Stem button group.
     */
    syncWinspacePropertyInputs(productForm, widthField, stemField) {
      this.upsertWinspacePropertyInput(
        productForm,
        'Anchura manillar',
        this.getWinspacePropertyValue(widthField)
      );
      this.upsertWinspacePropertyInput(
        productForm,
        'Longitud potencia',
        this.getWinspacePropertyValue(stemField)
      );
    }

    /**
     * Show/hide and populate Winspace handlebar property selectors.
     */
    updateWinspaceHandlebarProperties() {
      const propertyGroups = this.querySelectorAll('[data-winspace-handlebar-properties]');
      if (!propertyGroups.length) return;

      const productForm = this.getProductForm();

      propertyGroups.forEach((group) => {
        const optionIndex = group.dataset.optionIndex;
        const selector = optionIndex
          ? this.querySelector(`.option-selector[data-index="${optionIndex}"]`)
          : null;
        const integratedOption = selector
          ? selector.querySelector('[data-hyper2-integrated-option]:checked')
          : group.querySelector('[data-hyper2-integrated-option]:checked')
            || (group.hasAttribute('data-hyper2-standalone-properties')
              ? this.querySelector('[data-hyper2-integrated-option]:checked')
              : null);
        const selectedValue = integratedOption
          ? integratedOption.value
          : selector
            ? this.getSelectedValueForSelector(selector)
            : group.dataset.defaultHandlebar || null;
        const configKey = this.getWinspaceHandlebarConfigKey(selectedValue);
        const configScript = group.querySelector('[data-winspace-handlebar-config]');
        const widthField = group.querySelector('[data-winspace-property-width]');
        const stemField = group.querySelector('[data-winspace-property-stem]');
        const managesIntegratedHandlebar = group.hasAttribute('data-hyper2-standalone-properties')
          || Boolean(group.querySelector('[data-winspace-handlebar-selector]'))
          || Boolean(selector?.matches('[data-winspace-handlebar-selector]'));
        let config = null;

        if (configScript) {
          try {
            const parsedConfig = JSON.parse(configScript.textContent);
            config = configKey ? parsedConfig[configKey] : null;
          } catch (error) {
            config = null;
          }
        }

        if (!config) {
          group.hidden = true;
          [widthField, stemField].forEach((field) => this.setWinspacePropertyEnabled(field, false));
          if (managesIntegratedHandlebar) {
            this.upsertWinspacePropertyInput(
              productForm,
              'Manillar',
              integratedOption ? 'Hyper2.0' : group.dataset.defaultHandlebar || ''
            );
          }
          this.syncWinspacePropertyInputs(productForm, widthField, stemField);
          return;
        }

        this.populateWinspacePropertyField(widthField, config.widths || []);
        this.populateWinspacePropertyField(stemField, config.stems || []);

        group.hidden = false;
        [widthField, stemField].forEach((field) => {
          if (!field) return;
          this.setWinspacePropertyEnabled(field, true);

          if (!field.dataset.winspaceSyncBound) {
            field.addEventListener('change', () => {
              field.classList.remove('ws-field-invalid');
              field.querySelector('[data-ws-field-error]')?.setAttribute('hidden', '');
              this.syncWinspacePropertyInputs(productForm, widthField, stemField);
            });
            field.dataset.winspaceSyncBound = 'true';
          }
        });

        if (managesIntegratedHandlebar) {
          this.upsertWinspacePropertyInput(
            productForm,
            'Manillar',
            integratedOption ? 'Hyper2.0' : group.dataset.defaultHandlebar || ''
          );
        }
        this.syncWinspacePropertyInputs(productForm, widthField, stemField);
      });
    }

    /**
     * Initialize Winspace wheelset property selectors and sync to form.
     */
    initWinspaceWheelsetProperties() {
      const wheelsetContainer = this.querySelector('[data-winspace-wheelset-properties]');
      if (!wheelsetContainer) return;

      // Restore selections that were made before a DOM re-render (e.g. variant change)
      if (VariantPicker._wheelsetAppearance) {
        const input = wheelsetContainer.querySelector(`[data-winspace-wheelset-appearance][value="${CSS.escape(VariantPicker._wheelsetAppearance)}"]`);
        if (input) input.checked = true;
      }
      if (VariantPicker._wheelsetBrake) {
        const input = wheelsetContainer.querySelector(`[data-winspace-wheelset-brake][value="${CSS.escape(VariantPicker._wheelsetBrake)}"]`);
        if (input) input.checked = true;
      }

      const appearanceInputs = wheelsetContainer.querySelectorAll('[data-winspace-wheelset-appearance]');
      const brakeInputs = wheelsetContainer.querySelectorAll('[data-winspace-wheelset-brake]');

      appearanceInputs.forEach((input) => {
        if (!input.dataset.winspaceWheelsetBound) {
          input.addEventListener('change', () => {
            this.syncWinspaceWheelsetInputs(this.getProductForm(), wheelsetContainer);
          });
          input.dataset.winspaceWheelsetBound = 'true';
        }
      });

      brakeInputs.forEach((input) => {
        if (!input.dataset.winspaceWheelsetBound) {
          input.addEventListener('change', () => {
            this.syncWinspaceWheelsetInputs(this.getProductForm(), wheelsetContainer);
          });
          input.dataset.winspaceWheelsetBound = 'true';
        }
      });

      this.syncWinspaceWheelsetInputs(this.getProductForm(), wheelsetContainer);
    }

    /**
     * Sync Winspace wheelset property values to hidden form inputs.
     * @param {?HTMLFormElement} productForm - Product form element.
     * @param {HTMLElement} container - Wheelset properties container.
     */
    syncWinspaceWheelsetInputs(productForm, container) {
      const selectedAppearance = container.querySelector('[data-winspace-wheelset-appearance]:checked');
      const selectedBrake = container.querySelector('[data-winspace-wheelset-brake]:checked');

      // Persist selections so they survive DOM re-renders caused by variant changes
      if (selectedAppearance) VariantPicker._wheelsetAppearance = selectedAppearance.value;
      if (selectedBrake) VariantPicker._wheelsetBrake = selectedBrake.value;

      this.upsertWinspacePropertyInput(
        productForm,
        'Wheelset Appearance',
        selectedAppearance ? selectedAppearance.value : ''
      );
      this.upsertWinspacePropertyInput(
        productForm,
        'Wheelset Brake Type',
        selectedBrake ? selectedBrake.value : ''
      );
    }

    /**
     * Set status classes to help with styling.
     */
    updateStatusClasses() {
      const selectedOpts = this.getSelectedOptionIds();
      const nullCount = selectedOpts.filter((x) => x === null).length;
      const isSingleNullAtEnd = nullCount === 1 && selectedOpts[selectedOpts.length - 1] === null;
      this.classList.toggle('variant-picker--preselection', nullCount > 0);
      this.classList.toggle('variant-picker--pre-last-selection', nullCount > 0 && !isSingleNullAtEnd);
    }

    /**
     * Copies the selected value into a swatch label. (Required for pre-selection.)
     */
    updateSwatchLabel() {
      this.querySelectorAll('.option-selector:has(.js-color-text)').forEach((option) => {
        const label = option.querySelector('.js-color-text');
        const input = option.querySelector('.js-option:is(:checked, [aria-selected="true"])');
        label.textContent = input ? (input.dataset.displayValue || input.value) : '';
      });
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.availability === 'prune') {
        const toChange = this.querySelectorAll('.js-option.is-unavailable:is(:checked, [aria-selected="true"])');
        toChange.forEach((selected) => {
          const toSelect = selected.closest('.option-selector').querySelector('.js-option:not(.is-unavailable, [data-value=""])');
          if (toSelect) {
            setTimeout(() => {
              if (toSelect.closest('custom-select')) {
                toSelect.closest('custom-select').selectOption(toSelect);
              } else {
                toSelect.click();
              }
            }, 20); // Trigger after any updateContent logic has occurred
          }
        });
      }
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      const productForm = this.getProductForm();
      if (!productForm) return;

      this.addBtn = productForm.querySelector('[name="add"]');

      // Product not available
      if (!this.productAvailable) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noStock;
        return;
      }

      // Preselection
      if (this.getSelectedOptions().indexOf(null) !== -1) {
        if (this.addBtn.dataset.preselectionDisabled === 'true') {
          this.addBtn.disabled = true;
          this.addBtn.textContent = this.addBtn.dataset.preselectionText;
        } else {
          this.addBtn.disabled = false;
          this.addBtn.textContent = this.addBtn.dataset.addToCartText;
        }
        return;
      }

      // No variant
      if (!this.variant) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noVariant;
        return;
      }

      // Sold out
      if (!this.variant.available) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noStock;
        return;
      }

      // Add to cart
      this.addBtn.disabled = false;
      this.addBtn.textContent = this.addBtn.dataset.addToCartText;
    }

    /**
     * Updates the url with the selected variant id.
     */
    updateUrl() {
      if (this.dataset.updateUrl === 'false') return;
      const url = this.variant ? `${this.dataset.url}?variant=${this.variant.id}` : this.dataset.url;
      window.history.replaceState({ }, '', url);
    }

    /**
     * Gets the variant data for a product.
     * @returns {?object}
     */
    getVariantData() {
      const dataEl = this.querySelector(':scope > script[type="application/json"]:not([data-winspace-handlebar-config])');
      return dataEl ? JSON.parse(dataEl.textContent) : null;
    }

    /**
     * Get the selected values from a list of variant options.
     * @returns {Array} Array of selected option values, value is null if not selected.
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptions.push(selected && selected.dataset.value !== '' ? selected.dataset.value : null);
        } else {
          const selected = selector.querySelector('.js-option:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Get the selected option value ids from a list of variant options.
     * @returns {Array} Array of selected option value ids, value is null if not selected.
     */
    getSelectedOptionIds() {
      const selectedOptionIds = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const selected = selector.querySelector('.custom-select__option[aria-selected="true"]');
          selectedOptionIds.push(selected && selected.dataset.value !== '' ? selected.dataset.valueId : null);
        } else {
          const selected = selector.querySelector('.js-option:checked');
          selectedOptionIds.push(selected ? selected.dataset.valueId : null);
        }
      });

      return selectedOptionIds;
    }

    /**
     * Apply search parameters (set by product card swatches).
     */
    applySearchParams() {
      const searchParams = new URLSearchParams(window.location.search);

      Array.from(searchParams.keys()).forEach((key) => {
        const value = searchParams.get(key);
        const optionSelectors = Array.from(this.optionSelectors);
        const matchingOptionSelector = optionSelectors.find((x) => x.dataset.option === key);
        if (!matchingOptionSelector) return;

        if (matchingOptionSelector.dataset.selectorType === 'dropdown') {
          const colorOptionDropdown = matchingOptionSelector.querySelector(`.custom-select__option[data-value="${CSS.escape(value)}"]:not([aria-selected="true"])`);
          if (colorOptionDropdown) {
            const customSelect = colorOptionDropdown.closest('custom-select');
            const convertEvent = (evt) => {
              evt.detail.noFocus = true;
              customSelect.dispatchEvent(
                new CustomEvent('change', { bubbles: true, cancelable: false, detail: evt.detail })
              );
            };
            customSelect.addEventListener('change', convertEvent, { once: true });
            customSelect.selectOption(colorOptionDropdown);
          }
        } else {
          const matchingInput = matchingOptionSelector.querySelector(`input[value="${CSS.escape(value)}"]:not(:checked)`);
          if (matchingInput) {
            const matchingOptionValue = matchingOptionSelector.querySelector('.option-selector__label-value');
            if (matchingOptionValue) {
              matchingOptionSelector.querySelector('.option-selector__label-value').textContent = value;
            }
            matchingInput.checked = true;
            matchingInput.dispatchEvent(
              new CustomEvent('change', { bubbles: true, cancelable: false, detail: { noFocus: true } })
            );
          }
        }
      });
    }
  }

  customElements.define('variant-picker', VariantPicker);
}

(function initRaceitWinspaceCustomProperties() {
  if (window.__raceitWinspaceCustomPropsBound) return;
  window.__raceitWinspaceCustomPropsBound = true;

  const stateByRoot = {};

  function getRoot(element) {
    return element.closest('.js-quickbuy')
      || element.closest('.cc-featured-product')
      || element.closest('.shopify-section')
      || document;
  }

  function getState(element) {
    const root = getRoot(element);
    const key = root === document ? 'document' : root.id || root.dataset.sectionId || 'default';
    stateByRoot[key] = stateByRoot[key] || {};
    return stateByRoot[key];
  }

  function findProductForm(element) {
    const root = getRoot(element);
    return root.querySelector('.js-product-form')
      || root.querySelector('form[action*="/cart/add"]')
      || document.querySelector('.js-product-form')
      || document.querySelector('form[action*="/cart/add"]');
  }

  function findPickerForForm(form) {
    const root = getRoot(form);
    return root.querySelector('variant-picker');
  }

  function upsertPropertyInput(form, propertyName, value) {
    if (!form) return;
    let input = null;
    form.querySelectorAll('input[type="hidden"][data-winspace-property-name]').forEach((candidate) => {
      if (candidate.dataset.winspacePropertyName === propertyName) input = candidate;
    });
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${propertyName}]`;
      input.dataset.winspacePropertyName = propertyName;
      form.appendChild(input);
    }
    input.value = value || '';
  }

  function setPressed(options, activeOption, activeClass) {
    options.forEach((option) => {
      option.classList.toggle(activeClass, option === activeOption);
      option.setAttribute('aria-pressed', option === activeOption ? 'true' : 'false');
    });
  }

  function getFieldError(field) {
    const key = field.id || field.dataset.propertyName;
    let error = Array.from(field.querySelectorAll('[data-ws-field-error]')).find((candidate) => (
      candidate.dataset.wsFieldError === key
    ));
    if (!error) {
      error = document.createElement('div');
      error.className = 'ws-picker-error';
      error.dataset.wsFieldError = key;
      error.setAttribute('aria-live', 'polite');
      error.hidden = true;
      field.appendChild(error);
    }
    return error;
  }

  function showFieldError(field) {
    const error = getFieldError(field);
    error.textContent = field.dataset.requiredMessage || 'Please complete this required field.';
    error.hidden = false;
    field.classList.add('ws-field-invalid');
    field.setAttribute('aria-invalid', 'true');
  }

  function clearFieldError(field) {
    const key = field.id || field.dataset.propertyName;
    const error = Array.from(field.querySelectorAll('[data-ws-field-error]')).find((candidate) => (
      candidate.dataset.wsFieldError === key
    ));
    if (error) error.hidden = true;
    field.classList.remove('ws-field-invalid');
    field.removeAttribute('aria-invalid');
  }

  function selectWheel(option) {
    const container = option.closest('[data-winspace-hyper-properties]');
    if (!container) return;
    const grid = option.closest('.ws-hyper-grid');
    const wheelInput = container.querySelector('.ws-wheel-hidden-input');
    const value = option.dataset.wheelValue || '';

    setPressed(container.querySelectorAll('.ws-hyper-opt[data-wheel-value]'), option, 'ws-hyper-opt--active');
    if (wheelInput) wheelInput.value = value;
    if (grid) grid.classList.remove('is-invalid');
    const error = container.querySelector('[data-ws-wheel-error]');
    if (error) error.hidden = true;

    getState(container).wheel = value;
    upsertPropertyInput(findProductForm(container), 'Juego de ruedas', value);
  }

  function selectCrank(option) {
    const container = option.closest('[data-winspace-hyper-properties]');
    if (!container) return;
    const grid = option.closest('.ws-crank-grid');
    const crankInput = container.querySelector('.ws-crank-hidden-input');
    const value = option.dataset.crankValue || '';

    setPressed(grid.querySelectorAll('.ws-crank-opt[data-crank-value]'), option, 'ws-crank-opt--active');
    if (crankInput) crankInput.value = value;

    getState(container).crank = value;
    upsertPropertyInput(findProductForm(container), 'Longitud de biela', value);
  }

  function syncHyperProperties(container) {
    const form = findProductForm(container);
    const state = getState(container);
    let activeWheel = container.querySelector('.ws-hyper-opt--active[data-wheel-value]');
    let activeCrank = container.querySelector('.ws-crank-opt--active[data-crank-value]');
    const fixedCrank = container.querySelector('input[name="properties[Longitud de biela]"]');

    if (!activeWheel && state.wheel) {
      activeWheel = Array.from(container.querySelectorAll('.ws-hyper-opt[data-wheel-value]')).find((option) => (
        option.dataset.wheelValue === state.wheel
      ));
      if (activeWheel) setPressed(container.querySelectorAll('.ws-hyper-opt[data-wheel-value]'), activeWheel, 'ws-hyper-opt--active');
    }
    if (!activeCrank && state.crank) {
      activeCrank = Array.from(container.querySelectorAll('.ws-crank-opt[data-crank-value]')).find((option) => (
        option.dataset.crankValue === state.crank
      ));
      if (activeCrank) setPressed(container.querySelectorAll('.ws-crank-opt[data-crank-value]'), activeCrank, 'ws-crank-opt--active');
    }

    if (activeWheel) state.wheel = activeWheel.dataset.wheelValue || state.wheel;
    if (activeCrank) state.crank = activeCrank.dataset.crankValue || state.crank;

    const wheelValue = activeWheel ? activeWheel.dataset.wheelValue : '';
    const crankValue = activeCrank ? activeCrank.dataset.crankValue : (fixedCrank ? fixedCrank.value : '');
    const wheelInput = container.querySelector('.ws-wheel-hidden-input');
    const crankInput = container.querySelector('.ws-crank-hidden-input');

    if (wheelInput) wheelInput.value = wheelValue;
    if (crankInput) crankInput.value = crankValue;
    upsertPropertyInput(form, 'Juego de ruedas', wheelValue);
    upsertPropertyInput(form, 'Longitud de biela', crankValue);
  }

  function syncFixedCrankProperties(container) {
    const input = container.querySelector('[data-winspace-fixed-crank-input]');
    if (!input) return;
    upsertPropertyInput(findProductForm(container), 'Longitud de biela', input.value);
  }

  function syncHandlebarProperties(picker, form, result) {
    picker.querySelectorAll('[data-winspace-handlebar-properties]').forEach((group) => {
      if (group.hidden) return;
      const widthField = group.querySelector('[data-winspace-property-width]');
      const stemField = group.querySelector('[data-winspace-property-stem]');

      [widthField, stemField].forEach((field) => {
        if (!field || field.disabled) return;
        const propertyName = field.hasAttribute('data-winspace-property-width') ? 'Anchura manillar' : 'Longitud potencia';
        const selected = field.querySelector('[data-raceit-property-option]:checked');
        const value = selected?.value || '';
        upsertPropertyInput(form, propertyName, value);
        if (!value) {
          result.valid = false;
          result.firstInvalid = result.firstInvalid || field.querySelector('[data-raceit-property-option]:not([disabled])') || field;
          showFieldError(field);
        } else {
          clearFieldError(field);
        }
      });
    });
  }

  function validateProductForm(form) {
    const picker = findPickerForForm(form);
    if (!picker) return true;

    const result = { valid: true, firstInvalid: null };
    syncHandlebarProperties(picker, form, result);

    const hyperContainer = picker.querySelector('[data-winspace-hyper-properties]');
    if (hyperContainer) {
      syncHyperProperties(hyperContainer);
      const activeWheel = hyperContainer.querySelector('.ws-hyper-opt--active[data-wheel-value]');
      const grid = hyperContainer.querySelector('.ws-hyper-grid');
      const error = hyperContainer.querySelector('[data-ws-wheel-error]');

      if (!activeWheel) {
        result.valid = false;
        result.firstInvalid = result.firstInvalid || hyperContainer.querySelector('.ws-hyper-opt[data-wheel-value]');
        if (grid) grid.classList.add('is-invalid');
        if (error) error.hidden = false;
      } else {
        if (grid) grid.classList.remove('is-invalid');
        if (error) error.hidden = true;
      }
    }

    if (!result.valid && result.firstInvalid) {
      result.firstInvalid.scrollIntoView({ block: 'center', behavior: 'smooth' });
      result.firstInvalid.focus({ preventScroll: true });
    }

    return result.valid;
  }

  function syncAll() {
    document.querySelectorAll('[data-winspace-hyper-properties]').forEach(syncHyperProperties);
    document.querySelectorAll('[data-winspace-fixed-crank-properties]').forEach(syncFixedCrankProperties);
  }

  function scheduleSyncAll() {
    if (window.requestAnimationFrame) window.requestAnimationFrame(syncAll);
    window.setTimeout(syncAll, 80);
    window.setTimeout(syncAll, 350);
  }

  document.addEventListener('pointerdown', (event) => {
    const wheelOption = event.target.closest('.ws-hyper-opt[data-wheel-value]');
    if (wheelOption) selectWheel(wheelOption);
    const crankOption = event.target.closest('.ws-crank-opt[data-crank-value]');
    if (crankOption) selectCrank(crankOption);
  }, true);

  document.addEventListener('click', (event) => {
    const wheelOption = event.target.closest('.ws-hyper-opt[data-wheel-value]');
    if (wheelOption) {
      event.preventDefault();
      event.stopImmediatePropagation();
      selectWheel(wheelOption);
      return;
    }

    const crankOption = event.target.closest('.ws-crank-opt[data-crank-value]');
    if (crankOption) {
      event.preventDefault();
      event.stopImmediatePropagation();
      selectCrank(crankOption);
      return;
    }

    const addButton = event.target.closest('button[name="add"], input[name="add"], [type="submit"][name="add"]');
    if (!addButton) return;
    const form = addButton.closest('form') || findProductForm(addButton);
    if (form && !validateProductForm(form)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const wheelOption = event.target.closest('.ws-hyper-opt[data-wheel-value]');
    if (wheelOption) {
      event.preventDefault();
      selectWheel(wheelOption);
      return;
    }
    const crankOption = event.target.closest('.ws-crank-opt[data-crank-value]');
    if (crankOption) {
      event.preventDefault();
      selectCrank(crankOption);
    }
  }, true);

  document.addEventListener('change', (event) => {
    if (!event.target.closest('[data-winspace-handlebar-properties]')) {
      scheduleSyncAll();
      return;
    }
    event.stopImmediatePropagation();
    const picker = event.target.closest('variant-picker');
    if (!picker) return;
    syncHandlebarProperties(picker, findProductForm(picker), { valid: true, firstInvalid: null });
    scheduleSyncAll();
  }, true);

  document.addEventListener('invalid', (event) => {
    const field = event.target.closest('[data-raceit-property-field]');
    if (!field) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    showFieldError(field);
  }, true);

  document.addEventListener('on:variant:change', scheduleSyncAll);

  document.addEventListener('submit', (event) => {
    const { target: form } = event;
    if (!form.matches('.js-product-form, form[action*="/cart/add"]')) return;
    if (!validateProductForm(form)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  if ('MutationObserver' in window) {
    new MutationObserver((mutations) => {
      if (mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) => (
        node.nodeType === Node.ELEMENT_NODE
        && (node.matches?.('[data-winspace-hyper-properties], variant-picker')
          || node.querySelector?.('[data-winspace-hyper-properties], variant-picker'))
      )))) {
        scheduleSyncAll();
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  document.addEventListener('DOMContentLoaded', scheduleSyncAll);
  document.addEventListener('shopify:section:load', scheduleSyncAll);
  syncAll();
}());
